// Copyright (c) StrangeBee 2023

import React, { ReactElement, useContext, useMemo } from 'react';
import { ICONS } from '@Constants/iconsApp';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import { ProfileManage } from '@Organisms/Profile/ProfileManage/ProfileManage';
import { ObjectTypeV1 as ObjectType, AdminPermissionsEnum, ProfileOutput } from 'thehive-sdk';
import useConfigList from '@Hooks/useConfigList';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import client from '@Client';
import { DisplayTags } from '@Atoms/Display';
import ListProvider from '@Context/ListProvider';
import RouteBuilder from '@Services/RouteBuilder';
import { RouteType } from '@Types';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { computeEventList } from '@Organisms/Profile/eventList';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { useCurrentUser } from '@Queries/userQueries';
import usePermissions from '@Hooks/usePermissions';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { StatusContext } from '@Context/StatusProvider';
import { computeListSelector } from '@Organisms/Profile/selector';
import { useDrawer } from '@Hooks/useDrawer';
import usePageTitle from '@Hooks/usePageTitle';

import './ProfilesList.scss';

const ProfilesList = (): ReactElement => {
    const i = useLocalMessage();

    const config = useConfigList<ProfileOutput>({
        selector: computeListSelector(),
        controller: client.ProfileController,
        storageId: StorageFilterEnum.PROFILE,
        objectType: ObjectType.PROFILE,
    });

    const { licenseCapabilities } = useContext(StatusContext);
    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer({});

    const customProfilesCapability = useMemo<boolean>(
        () => Object.keys(licenseCapabilities.users || {}).includes('customProfiles'),
        [licenseCapabilities],
    );

    const {
        dataSource = [],
        columnsRenderer,
        listRefetch,
        countRefetch,
        table,
        status = 'loading',
        filterManager,
        paginationManager,
        storageViewManager,
        storagePageManager,
    } = config;

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.ProfileController,
                locale: i,
                onSuccess: (count, message) => {
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
                onError: handleNotificationError,
            }),
        [listRefetch, countRefetch, i],
    );

    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const hasPermissions = usePermissions({
        permissionsNeeded: [AdminPermissionsEnum.MANAGE_PROFILE],
        permissionsList: permissions,
    });

    const columns = columnsRenderer([
        {
            title: [{ label: i('profile.list.table.columns.name'), sortField: 'name' }],
            className: 'profiles-list__name column_preview',
            render: (profile): ReactElement => <div className="profiles-list__name__title">{profile.name}</div>,
        },
        {
            title: i('profile.list.table.columns.permissions'),
            className: 'profiles-list__permissions',
            render: ({ permissions = [] }, { custom: filterCustom }): ReactElement =>
                permissions.length !== 0 ? (
                    <DisplayTags
                        tags={permissions}
                        onClick={(tagValue): void =>
                            filterCustom({ field: 'permissions', operator: 'any', values: [tagValue] })
                        }
                    />
                ) : (
                    <i>{i('profile.permissions.empty')}</i>
                ),
        },
        {
            hidden: !customProfilesCapability,
            className: 'profiles-list__actions',
            render: (profile): ReactElement | null =>
                hasPermissions ? (
                    <ActionsList
                        actionList={{
                            edit: {
                                label: i('general.edit'),
                                icon: <ICONS.EditIcon />,
                                hidden: !profile.editable,
                                CustomComponent: (triggerElem) => (
                                    <ProfileManage
                                        triggerElem={triggerElem}
                                        data={profile}
                                        onFinish={eventList.onEdit}
                                    />
                                ),
                            },
                            delete: {
                                label: i('general.delete'),
                                confirm: true,
                                disabled: !profile.editable,
                                message: i('profile.delete'),
                                query: () => eventList.onDelete(profile._id),
                                icon: <ICONS.DeleteIcon />,
                            },
                        }}
                        isIcon
                        scope="single"
                    />
                ) : null,
        },
    ]);

    const { data: describe } = useListAllFields();

    usePageTitle(i('page-title.admin.entities.profiles'));

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions && (
                            <AddButton
                                label={i('profile.list.header.add-profile')}
                                disabled={!customProfilesCapability}
                                onClick={() => setVisibleCreate(true)}
                            />
                        )}
                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        <ManageSwitch
                            label={i('common.filters')}
                            value={filterManager.editFilters}
                            onChange={(editFilters): void => {
                                filterManager.setEditFilters(editFilters);
                            }}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                            <THFilter filterConfig={filterManager} describeField={describe?.[ObjectType.PROFILE]}>
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('profile.none')}>
                            {hasPermissions && (
                                <THButton type="link" onClick={() => setVisibleCreate(true)}>
                                    {i('profile.list.header.add-profile')}{' '}
                                </THButton>
                            )}
                        </DisplayEmptyData>
                    }
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>
            {visibleCreate && (
                <ProfileManage
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    onFinish={eventList.onCreate}
                />
            )}
        </>
    );
};

const path = RouteBuilder.compute('administration.profiles');

export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.profiles',
        icon: <ICONS.UserProfileIcon />,
    },
    breadcrumb: { label: 'breadcrumb.global.profiles' },
    content: <ProfilesList />,
    Context: ListProvider,
    space: ['admin'],
    permissionNeeded: [AdminPermissionsEnum.MANAGE_PROFILE],
    exact: true,
};
